import styled, { keyframes } from "styled-components";
import {
  White,
  DarkGrey,
  OffWhite,
  White05,
  DarkGrey2,
  White08,
  Blue,
  Purple,
  Grey,
  Black,
} from "../theme/colors";
import { P3 } from "../theme/text/Headings";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  animation: ${pulse} 2s infinite;
`;

export const InsightsText = styled.div`
  text-align: left;
  color: ${White};
  padding: 0 35px;
  margin-bottom: 14px;
`;

export const QuizSectionContainer = styled.div`
  margin-bottom: 20px;
`;

// Profile.js styles
export const InfoBox = styled.div`
  border-radius: 10px;
  margin: 0px 0px 0 10px;
  width: 100%;
  color: ${White};
`;

export const FriendBox = styled.div`
  border-radius: 10px;
  padding: 10px 0;
  margin: 10px 0 10px 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  color: ${White};
`;

export const TraitsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 20px;
  color: ${White};
`;

export const TraitItem = styled.div`
  background: ${DarkGrey};
  padding: 10px 15px 10px 15px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

// Summary.js styles
export const SummaryContainer = styled.div`
  border-radius: 20px;
  padding: 20px;
  width: 100%;
`;

export const SummaryHeader = styled.div`
  margin-bottom: 0px;
`;

export const TabContainer = styled.div`
  display: flex;
`;

export const Tab = styled.button`
  background: ${(props) => (props.$isActive ? DarkGrey : DarkGrey2)};
  border: none;
  padding: 10px 20px 7px;
  border-radius: 15px 15px 0 0;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.$isActive ? White : White05)};
  transition: all 0.2s ease;

  &:hover {
    color: ${White};
  }
`;

export const TabContent = styled.div`
  min-height: 200px;
  background: ${DarkGrey};
  border-radius: 0 15px 15px 15px;
  padding: 12.5px 20px;
`;

export const TraitRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12.5px 0;

  &:last-child {
    border-bottom: none;
  }
`;

export const TraitValueContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 2;
  justify-content: flex-end;
  gap: 4px;
`;

export const TraitOption = styled.span`
  color: ${(props) => (props.$isSelected ? DarkGrey : White08)};
  font-weight: ${(props) => (props.$isSelected ? 800 : 400)};
  background: ${(props) => (props.$isSelected ? White08 : "transparent")};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 13px;
  opacity: ${(props) => (props.$isSelected ? 1 : 0.8)};
  transition: all 0.2s ease;
`;

export const TraitSeparator = styled.span`
  color: ${White};
  opacity: 0.3;
  font-size: 12px;
  margin: 0 2px;
`;

export const LoadingRow = styled(TraitRow)`
  opacity: 0.5;
`;

export const QuizzesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

export const QuizCardBorder = styled.div`
  border-radius: 16px;
  padding: 3px; /* border thickness */
  background: linear-gradient(
    125deg,
    ${(props) => props.$c1 || "#666"},
    ${(props) => props.$c2 || "#999"}
  );
`;

export const QuizCardInner = styled.div`
  border-radius: 16px;
  padding: 14px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  user-select: none;
`;

export const QuizCardTitle = styled(P3)`
  color: ${OffWhite};
  line-height: 1.3;
  font-weight: 900;
`;

// Slider components for new summary design
export const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 0;
`;

export const SliderTitle = styled.div`
  color: ${White};
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
`;

export const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 13px;
  background: ${Black};
  border-radius: 20px;
  overflow: hidden;
`;

export const SliderFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${(props) => props.$percentage || 0}%;
  background: linear-gradient(90deg, ${Blue} 0%, ${Purple} 100%);
  border-radius: 20px;
  transition: width 0.3s ease;
`;

export const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
`;

export const SliderLabel = styled.span`
  color: ${White08};
  font-size: 13px;
  font-weight: 400;
`;

// Copy Link Button (used in Profile and PublicProfile)
export const CopyLinkButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 6px;
  color: ${Grey};
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 32px;
  height: 32px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &.copied {
    border-color: rgba(0, 255, 0, 0.4);
    color: rgba(0, 255, 0, 0.8);
  }
`;
